import React, {
  type ReactElement,
  createContext,
  useContext,
  useState,
  ReactNode,
} from "react";
import { graphql, useMutation } from "react-relay";
import { useRouter } from "next/router";
import { ConnectionHandler, RecordSourceSelectorProxy } from "relay-runtime";
import logger from "@olivahealth/logger/client";
import { CREATE_THREAD_TRACK_FROM } from "@olivahealth/constants";
import { useUserData } from "./UserDataContext";
import { useAmplitude } from "./AmplitudeContext";
import { ChatContextCompanionWidgetMutation as ICompanionWidgetMutation } from "./__generated__/ChatContextCompanionWidgetMutation.graphql";
import { ChatContextPopularTopicsMutation as IPopularTopicsMutation } from "./__generated__/ChatContextPopularTopicsMutation.graphql";
import { ChatContextReflectionMutation as IReflectionMutation } from "./__generated__/ChatContextReflectionMutation.graphql";

const CompanionWidgetMutation = graphql`
  mutation ChatContextCompanionWidgetMutation(
    $userId: ID!
    $message: String!
    $from: String
    $careModality: CompanionCareModality
    $threadType: ThreadType
  ) {
    createThread(
      userId: $userId
      initialMessage: $message
      from: $from
      careModality: $careModality
      threadType: $threadType
    ) {
      __typename
      ... on ThreadCreated {
        threadId
        threadTitle
        threadType
        chatChannelUrl
      }
    }
  }
`;

const PopularTopicsMutation = graphql`
  mutation ChatContextPopularTopicsMutation(
    $userId: ID!
    $message: String!
    $from: String!
    $careModality: CompanionCareModality!
    $threadType: ThreadType
  ) {
    createThread(
      userId: $userId
      from: $from
      initialMessage: $message
      careModality: $careModality
      threadType: $threadType
    ) {
      __typename
      ... on ThreadCreated {
        threadId
        threadTitle
        threadType
        chatChannelUrl
      }
    }
  }
`;

const ReflectionMutation = graphql`
  mutation ChatContextReflectionMutation(
    $userId: ID!
    $message: String!
    $from: String!
    $reflectionAnswer: String
  ) {
    createThread(
      userId: $userId
      initialMessage: $message
      from: $from
      reflectionAnswer: $reflectionAnswer
    ) {
      __typename
      ... on ThreadCreated {
        threadId
        threadTitle
        threadType
        chatChannelUrl
      }
    }
  }
`;

interface ChatContextType {
  isCreatingThread: boolean;
  createCompanionThread: (
    message: string,
    from: (typeof CREATE_THREAD_TRACK_FROM)[keyof typeof CREATE_THREAD_TRACK_FROM],
    onError?: () => void,
    careModality?: "THERAPY" | "PROFESSIONAL_COACHING",
    threadType?: "WORK" | "WELLBEING",
  ) => void;
  createPopularTopicsThread: (
    message: string,
    careModality: "THERAPY" | "PROFESSIONAL_COACHING",
    from?: (typeof CREATE_THREAD_TRACK_FROM)[keyof typeof CREATE_THREAD_TRACK_FROM],
    onError?: () => void,
    threadType?: "WORK" | "WELLBEING",
  ) => void;
  createReflectionThread: (
    message: string,
    reflectionType:
      | typeof CREATE_THREAD_TRACK_FROM.POST_SESSION_8_REFLECTION
      | typeof CREATE_THREAD_TRACK_FROM.PRE_SESSION_12_REFLECTION,
    reflectionAnswer?: string,
    onError?: () => void,
  ) => void;
}

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export function useChatContext(): ChatContextType {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
}

interface ChatProviderProps {
  children: ReactNode;
}

export function ChatProvider({ children }: ChatProviderProps): ReactElement {
  const { data: user } = useUserData();
  const { trackEvent } = useAmplitude();
  const router = useRouter();
  const [isCreatingThread, setIsCreatingThread] = useState(false);

  const [companionWidgetMutate] = useMutation<ICompanionWidgetMutation>(
    CompanionWidgetMutation,
  );
  const [popularTopicsMutate] = useMutation<IPopularTopicsMutation>(
    PopularTopicsMutation,
  );
  const [reflectionMutate] =
    useMutation<IReflectionMutation>(ReflectionMutation);

  const createThreadUpdater = (
    store: RecordSourceSelectorProxy<
      (
        | ICompanionWidgetMutation
        | IPopularTopicsMutation
        | IReflectionMutation
      )["response"]
    >,
    response:
      | (
          | ICompanionWidgetMutation
          | IPopularTopicsMutation
          | IReflectionMutation
        )["response"]
      | null
      | undefined,
  ) => {
    if (
      !response?.createThread ||
      response.createThread.__typename !== "ThreadCreated"
    ) {
      return;
    }

    const threadsConnection = store.get(
      'client:root:threads(first:3,type:"WELLBEING")',
    );
    const threadsAllConnection = store.get(
      'client:root:threads(first:10,type:"WELLBEING")',
    );

    const threadRecord = store.create(response.createThread.threadId, "Thread");

    threadRecord.setValue(new Date().toISOString(), "createdAt");
    threadRecord.setValue(response.createThread.threadId, "id");
    threadRecord.setValue(response.createThread.threadTitle, "title");
    threadRecord.setValue(response.createThread.threadType, "type");

    if (threadsConnection) {
      const newEdge = ConnectionHandler.createEdge(
        store,
        threadsConnection,
        threadRecord,
        "Thread",
      );
      ConnectionHandler.insertEdgeBefore(threadsConnection, newEdge);
    }

    if (threadsAllConnection) {
      const newEdgeAllConnection = ConnectionHandler.createEdge(
        store,
        threadsAllConnection,
        threadRecord,
        "Thread",
      );
      ConnectionHandler.insertEdgeBefore(
        threadsAllConnection,
        newEdgeAllConnection,
      );
    }

    const linkedRecords = threadsConnection?.getLinkedRecords("edges");
    if (!linkedRecords) {
      return;
    }

    const lastEdge = linkedRecords[linkedRecords.length - 1];
    const lastNode = lastEdge.getLinkedRecord("node");

    if (lastNode && threadsConnection) {
      ConnectionHandler.deleteNode(
        threadsConnection,
        lastNode.getValue("id")?.toString() ?? "",
      );
    }
  };

  const createCompanionThread = (
    message: string,
    from: (typeof CREATE_THREAD_TRACK_FROM)[keyof typeof CREATE_THREAD_TRACK_FROM],
    onError?: () => void,
    careModality?: "THERAPY" | "PROFESSIONAL_COACHING",
    threadType?: "WORK" | "WELLBEING",
  ) => {
    if (!user?.id) {
      return;
    }

    setIsCreatingThread(true);

    // Track conversation started
    if (from === CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK) {
      // Determine the source based on current path
      const currentPath = router.asPath;
      const isTeamMemberDetailsPage = currentPath.includes("/work/team/");

      trackEvent("Oli for work conversation started", {
        from: isTeamMemberDetailsPage ? "team_member_details" : "home",
      });
    } else {
      // For other chat sources, we can track general conversation started events
      // but this is specifically for Oli for Work tracking
    }

    companionWidgetMutate({
      variables: {
        userId: user.id,
        message,
        from: from as string,
        careModality,
        threadType,
      },
      onError: (error) => {
        setIsCreatingThread(false);
        logger.error(
          "ChatContext/createCompanionThread",
          "Failed to create thread",
          {
            userId: user.id,
            message,
            errorMessage: (error as Error).message,
            errorStack: (error as Error).stack,
          },
        );
        onError?.();
      },
      onCompleted: (response, errors) => {
        if (response.createThread.__typename !== "ThreadCreated" || errors) {
          setIsCreatingThread(false);
          onError?.();
          return;
        }

        const threadId = response.createThread.threadId;
        if (from === CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK) {
          router.push(`/work/chat/${threadId}`).then(() => {
            setIsCreatingThread(false);
          });
        } else {
          router.push(`/companion/chat/${threadId}`).then(() => {
            setIsCreatingThread(false);
          });
        }
      },
      updater: createThreadUpdater,
    });
  };

  const createPopularTopicsThread = (
    message: string,
    careModality: "THERAPY" | "PROFESSIONAL_COACHING",
    from?: (typeof CREATE_THREAD_TRACK_FROM)[keyof typeof CREATE_THREAD_TRACK_FROM],
    onError?: () => void,
    threadType?: "WORK" | "WELLBEING",
  ) => {
    if (!user?.id) {
      return;
    }

    setIsCreatingThread(true);

    popularTopicsMutate({
      variables: {
        userId: user.id,
        message,
        from: from || CREATE_THREAD_TRACK_FROM.POPULAR_TOPICS,
        careModality,
        threadType,
      },
      onError: (error) => {
        setIsCreatingThread(false);
        logger.error(
          "ChatContext/createPopularTopicsThread",
          "Failed to create thread",
          {
            userId: user.id,
            message,
            careModality,
            from,
            errorMessage: (error as Error).message,
            errorStack: (error as Error).stack,
          },
        );
        onError?.();
      },
      onCompleted: (response) => {
        if (response.createThread?.__typename === "ThreadCreated") {
          const { threadId } = response.createThread;
          if (from === CREATE_THREAD_TRACK_FROM.OLIVA_AT_WORK) {
            router.push(`/work/chat/${threadId}`).then(() => {
              setIsCreatingThread(false);
            });
          } else {
            router.push(`/companion/chat/${threadId}`).then(() => {
              setIsCreatingThread(false);
            });
          }
        } else {
          setIsCreatingThread(false);
          onError?.();
        }
      },
      updater: createThreadUpdater,
    });
  };

  const createReflectionThread = (
    message: string,
    reflectionType:
      | typeof CREATE_THREAD_TRACK_FROM.POST_SESSION_8_REFLECTION
      | typeof CREATE_THREAD_TRACK_FROM.PRE_SESSION_12_REFLECTION,
    reflectionAnswer?: string,
    onError?: () => void,
  ) => {
    if (!user?.id) {
      return;
    }

    setIsCreatingThread(true);

    const from =
      reflectionType === CREATE_THREAD_TRACK_FROM.POST_SESSION_8_REFLECTION
        ? CREATE_THREAD_TRACK_FROM.POST_SESSION_8_REFLECTION
        : CREATE_THREAD_TRACK_FROM.PRE_SESSION_12_REFLECTION;

    reflectionMutate({
      variables: {
        userId: user.id,
        message,
        from: from as string,
        reflectionAnswer: reflectionAnswer,
      },
      onError: (error) => {
        setIsCreatingThread(false);
        logger.error(
          "ChatContext/createReflectionThread",
          "Failed to create thread",
          {
            userId: user.id,
            message,
            reflectionType,
            reflectionAnswer,
            errorMessage: (error as Error).message,
            errorStack: (error as Error).stack,
          },
        );
        onError?.();
      },
      onCompleted: (response, errors) => {
        if (response.createThread.__typename !== "ThreadCreated" || errors) {
          setIsCreatingThread(false);
          onError?.();
          return;
        }

        const threadId = response.createThread.threadId;
        router.push(`/companion/chat/${threadId}`).then(() => {
          setIsCreatingThread(false);
        });
      },
      updater: createThreadUpdater,
    });
  };

  const value: ChatContextType = {
    isCreatingThread,
    createCompanionThread,
    createPopularTopicsThread,
    createReflectionThread,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
